import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { FormAxios } from "../../axios/form/form.axios";
import { Form } from "../../model/form/Form";
import { FormAssignDto } from "../../model/form/dto/AssignFormDto";
import { CloneFormDto } from "../../model/form/dto/CloneFormDto";
import { ConfigureFormDto } from "../../model/form/dto/ConfigureFormDto";
import { CreateFormDto } from "../../model/form/dto/CreateFormDto";
import { DeleteFormDto } from "../../model/form/dto/DeleteFormDto";
import { UpdateFormDto } from "../../model/form/dto/UpdateFormDto";
import { AtiraThunk } from "../AtiraThunk";
import { userActions } from "../user/user.slice";
import { GetUserFormsDto } from "../../model/form/dto/GetUserFormsDto";
import { CreateFormsFolderDto } from "../../model/form/dto/CreateFormsFolderDto";
import { UpdateFormsFolderDto } from "../../model/form/dto/UpdateFormsFolderDto";
import { DeleteFormsFolderDto } from "../../model/form/dto/DeleteFormsFolderDto";
import { FormsFolder } from "../../model/form/types/FormsFolder";
import { GetFormFoldersDto } from "../../model/form/dto/GetFormFoldersDto";
import { AddRemoveFormFromToFolderDto } from "../../model/form/dto/AddRemoveFormFromToFolderDto";
import { revertAllReduxAction } from "../store-reset";
import { TabKeys } from "../../routes/form/types/FormTabs.enum";

interface FormReducer {
  forms: Form[];
  formsLoading: boolean;
  folders?: FormsFolder[];
  foldersLoading: boolean;
  //
  activeTab: TabKeys;
}

const initialState = Object.freeze<FormReducer>({
  forms: [],
  formsLoading: false,
  folders: [],
  foldersLoading: false,
  activeTab: TabKeys.FORMS,
});

const getForms = AtiraThunk<Form[], GetUserFormsDto>(
  `/forms/all`,
  FormAxios.getForms,
);

const createForm = AtiraThunk<void, CreateFormDto>(
  `/forms/create`,
  FormAxios.createForm,
);

const deleteForm = AtiraThunk<void, DeleteFormDto>(
  `/form/delete`,
  FormAxios.deleteForm,
);

const updateForm = AtiraThunk<void, UpdateFormDto>(
  `/form/update`,
  FormAxios.updateForm,
);

const cloneForm = AtiraThunk<Form, CloneFormDto>(`/form/{id}/clone`, (dto) =>
  FormAxios.cloneForm(dto),
);

const assignForm = AtiraThunk<void, FormAssignDto>(
  `/form/assign`,
  FormAxios.assignForm,
);

const configureForm = AtiraThunk<void, ConfigureFormDto>(
  `/form/:id/configure`,
  FormAxios.configureForm,
);

const createFolder = AtiraThunk<void, CreateFormsFolderDto>(
  "/form/folder/create",
  FormAxios.createFolder,
);

const getFolders = AtiraThunk<FormsFolder[], GetFormFoldersDto>(
  "/form/folder",
  FormAxios.getFolders,
);

const updateFolder = AtiraThunk<void, UpdateFormsFolderDto>(
  "/form/folder/update",
  FormAxios.updateFolder,
);

const deleteFolder = AtiraThunk<void, DeleteFormsFolderDto>(
  "/form/folder/delete",
  FormAxios.deleteFolder,
);

const addRemoveFormToFolder = AtiraThunk<void, AddRemoveFormFromToFolderDto>(
  "/form/:id/folder/:formFolderId/",
  FormAxios.addRemoveFormToFolder,
);

const formSlice = createSlice({
  name: "form",
  initialState,
  reducers: {
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getForms.pending, (state) => {
      state.formsLoading = true;
    });
    addCase(getForms.fulfilled, (state, action) => {
      state.forms = action.payload;
      state.formsLoading = false;
    });
    addCase(getForms.rejected, (state) => {
      state.formsLoading = false;
    });

    addCase(getFolders.pending, (state) => {
      state.foldersLoading = true;
    });
    addCase(getFolders.fulfilled, (state, action) => {
      state.folders = action.payload;
      state.foldersLoading = false;
    });
    addCase(getFolders.rejected, (state) => {
      state.foldersLoading = false;
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const formActions = {
  getForms,
  createForm,
  deleteForm,
  updateForm,
  cloneForm,
  assignForm,
  configureForm,
  createFolder,
  getFolders,
  updateFolder,
  deleteFolder,
  addRemoveFormToFolder,
  ...formSlice.actions,
};

export default formSlice.reducer;
